/**
 * SaasAdmin.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Self-contained SaaS-Admin console, ported from the Angular
 * `saas-admin` feature module. Rendered as a full-screen overlay on top of the
 * existing support console (opened from a button in the Topbar), so it does not
 * disturb any existing app state.
 *
 * Internal navigation replaces the Angular router: a small `route` state maps to
 * each page component, mirroring saas-admin-routing.module.ts.
 */
import React, { useEffect, useState } from 'react';
import './saas-admin.css';

import { AdminOverview } from './pages/AdminOverview';
import { Tenants } from './pages/Tenants';
import { UsageTracking } from './pages/UsageTracking';
import { RemindersEngine } from './pages/RemindersEngine';
import { PaymentCollection } from './pages/PaymentCollection';
import { InvoicesReceipts } from './pages/InvoicesReceipts';
import { UiFeatures } from './pages/UiFeatures';
import { PricingConfig } from './pages/PricingConfig';
import { TrialVisitors } from './pages/TrialVisitors';
import { ImportLimits } from './pages/ImportLimits';
import { BackgroundJobs } from './pages/BackgroundJobs';

export type AdminRoute =
  | 'overview' | 'tenants' | 'usage' | 'reminders' | 'payments' | 'invoices' | 'ui-features'
  | 'pricing' | 'trial-visitors' | 'import-limits' | 'jobs';

const NAV_ITEMS: { route: AdminRoute; icon: string; label: string }[] = [
  { route: 'overview',    icon: 'dashboard',     label: 'Overview' },
  { route: 'tenants',     icon: 'apartment',     label: 'Tenants' },
  { route: 'usage',       icon: 'monitor_heart', label: 'Usage tracking' },
  { route: 'reminders',   icon: 'campaign',      label: 'Reminders' },
  { route: 'payments',    icon: 'paid',          label: 'Payments' },
  { route: 'invoices',    icon: 'receipt_long',  label: 'Invoices' },
  { route: 'ui-features', icon: 'account_tree',  label: 'UI Feature' },
  { route: 'pricing',     icon: 'sell',          label: 'Pricing & Credits' },
  // Trial visitors used to be a section at the bottom of Pricing & Credits, and
  // could only show the BLOCKED ones. It is its own page now because the first
  // question an agent has is "is this person even in here?", which a
  // blocked-only list cannot answer. Last in the list because it is a support
  // tool rather than part of the billing story above it.
  { route: 'trial-visitors', icon: 'groups',      label: 'Trial visitors' },
  // Application settings sit last and apart: everything above answers a
  // question about a tenant, this one changes how the product behaves for all
  // of them. Internal operators only — a tenant who can raise their own limit
  // is not limited.
  { route: 'import-limits',  icon: 'tune',        label: 'Import limits' },
  // Health of the nightly and queue jobs behind credits (reconcile, S3 archives,
  // charging). Read-only; for the support team to spot problems without Postman.
  { route: 'jobs',           icon: 'fact_check',  label: 'Background jobs' },
];

// Load Material Icons + the admin fonts once, without touching index.html.
function ensureFonts() {
  const links: { id: string; href: string }[] = [
    { id: 'lp-admin-material-icons', href: 'https://fonts.googleapis.com/icon?family=Material+Icons' },
    { id: 'lp-admin-fonts', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap' },
  ];
  links.forEach(({ id, href }) => {
    if (!document.getElementById(id)) {
      const l = document.createElement('link');
      l.id = id;
      l.rel = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
    }
  });
}

export const SaasAdmin: React.FC<{ onExit: () => void }> = ({ onExit }) => {
  const [route, setRoute] = useState<AdminRoute>('overview');
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => { ensureFonts(); }, []);

  const renderPage = () => {
    switch (route) {
      case 'overview':    return <AdminOverview onNavigate={setRoute} />;
      case 'tenants':     return <Tenants />;
      case 'usage':       return <UsageTracking />;
      case 'reminders':   return <RemindersEngine />;
      case 'payments':    return <PaymentCollection />;
      case 'invoices':    return <InvoicesReceipts />;
      case 'ui-features': return <UiFeatures />;
      case 'pricing':     return <PricingConfig />;
      case 'trial-visitors': return <TrialVisitors />;
      case 'import-limits': return <ImportLimits />;
      case 'jobs':        return <BackgroundJobs />;
      default:            return <AdminOverview onNavigate={setRoute} />;
    }
  };

  return (
    <div
      className="lp-admin-root"
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#e6ecf2' }}
    >
      <div className={`admin-shell${collapsed ? ' admin-shell--collapsed' : ''}`}>

        {/* Sidebar */}
        <aside className="admin-aside">
          <div className="admin-aside__brand">
            <span className="admin-aside__logo"><span className="material-icons">shield</span></span>
            {!collapsed && (
              <div className="admin-aside__brand-text">
                <div className="admin-aside__brand-name">Logipulse</div>
                <div className="admin-aside__brand-sub">Admin console</div>
              </div>
            )}
          </div>

          <nav className="admin-nav">
            {NAV_ITEMS.map(item => (
              <button
                key={item.route}
                className={`admin-nav__item${route === item.route ? ' admin-nav__item--active' : ''}`}
                onClick={() => setRoute(item.route)}
              >
                <span className="material-icons admin-nav__icon">{item.icon}</span>
                {!collapsed && <span className="admin-nav__label">{item.label}</span>}
              </button>
            ))}
          </nav>

          <button className="admin-aside__toggle" onClick={() => setCollapsed(c => !c)}>
            <span className="material-icons">{collapsed ? 'chevron_right' : 'chevron_left'}</span>
          </button>
        </aside>

        {/* Top bar + content */}
        <div className="admin-main">
          <header className="admin-topbar">
            <div className="admin-topbar__search">
              <span className="material-icons">search</span>
              <input type="text" placeholder="Search tenants, invoices, templates…" />
            </div>

            <div className="admin-topbar__right">
              <button className="admin-topbar__btn">
                <span className="material-icons">notifications</span>
                <span className="admin-topbar__badge">5</span>
              </button>
              <button className="admin-topbar__btn"><span className="material-icons">help</span></button>
              <button className="admin-topbar__exit" onClick={onExit} title="Return to the support console">
                <span className="material-icons">arrow_back</span>
                Back to Support
              </button>
              <div className="admin-topbar__user">
                <div className="admin-topbar__avatar">AD</div>
                <div className="admin-topbar__user-info">
                  <div className="admin-topbar__user-name">Admin User</div>
                  <div className="admin-topbar__user-role">Logipulse staff</div>
                </div>
              </div>
            </div>
          </header>

          <main className="admin-content">
            {renderPage()}
          </main>
        </div>
      </div>
    </div>
  );
};
